import { permissionRoles } from "../config";
import type { PermissionRole, Role } from "../types";
import {
  PERMISSION_ROLE_KEY,
  PERMISSION_TOUCHED_KEY,
  storageGet,
  storageSet,
} from "./workspaceStorage";

export const roleLabels: Record<Role, string> = {
  supplier: "Supplier",
  restaurant: "Restaurant",
  admin: "Network admin",
};

export const roleSwitchLabels: Record<Role, string> = {
  supplier: "Supplier",
  restaurant: "Restaurant",
  admin: "Admin",
};

export function sidebarWorkspaceCopy(role: Role) {
  if (role === "supplier") {
    return {
      label: "From your stock to their kitchen.",
      title: "Price requests, private offers, orders, and invoices.",
      detail: "Start with the restaurant waiting for a price.",
    };
  }

  if (role === "admin") {
    return {
      label: "Every route, every market.",
      title: "Coverage, incidents, suppliers, and market health.",
      detail: "Open the weakest route or category first.",
    };
  }

  return {
    label: "From supplier to your kitchen.",
    title: "Today's list, usual suppliers, invoices, and issues.",
    detail: "Start with what you need for service.",
  };
}

export function searchPlaceholderForRole(role: Role) {
  if (role === "supplier") {
    return "Search products, restaurants, orders";
  }

  if (role === "admin") {
    return "Search suppliers, markets, incidents";
  }

  return "Search items, suppliers, invoices";
}

export function permissionOptionsForRole(role: Role) {
  if (role === "supplier") {
    return permissionRoles.filter((permission) => permission.id === "supplier-sales");
  }

  if (role === "admin") {
    return permissionRoles.filter((permission) => permission.id === "owner" || permission.id === "finance");
  }

  return permissionRoles.filter((permission) => permission.id !== "supplier-sales");
}

export function defaultPermissionRoleId(role: Role) {
  if (role === "supplier") {
    return "supplier-sales";
  }

  if (role === "admin" || role === "restaurant") {
    return "owner";
  }

  return "owner";
}

export function permissionRoleStorageKey(role: Role) {
  return `${PERMISSION_ROLE_KEY}.${role}`;
}

export function permissionTouchedStorageKey(role: Role) {
  return `${PERMISSION_TOUCHED_KEY}.${role}`;
}

export function initialPermissionRoleId(role: Role) {
  const storedForRole = storageGet(permissionRoleStorageKey(role));
  const roleTouched = storageGet(permissionTouchedStorageKey(role)) === "true";

  if (role === "supplier" && storedForRole === "owner" && !roleTouched) {
    storageSet(permissionRoleStorageKey(role), "supplier-sales");
    storageSet(permissionTouchedStorageKey(role), "migrated-supplier-default");
    return "supplier-sales";
  }

  if (storedForRole && permissionOptionsForRole(role).some((permission) => permission.id === storedForRole)) {
    return storedForRole;
  }

  const stored = storageGet(PERMISSION_ROLE_KEY);

  if (
    role === "restaurant" &&
    stored === "buyer" &&
    storageGet(PERMISSION_TOUCHED_KEY) !== "true" &&
    !roleTouched
  ) {
    storageSet(permissionRoleStorageKey(role), "owner");
    storageSet(permissionTouchedStorageKey(role), "migrated-owner-default");
    return "owner";
  }

  if (role !== "supplier" && stored && permissionOptionsForRole(role).some((permission) => permission.id === stored)) {
    return stored;
  }

  return defaultPermissionRoleId(role);
}

export function permissionCapabilitySummary(permission: PermissionRole) {
  const capabilities = [
    permission.canSubmitOrders ? "orders" : null,
    permission.canApproveOrders ? "approvals" : null,
    permission.canManageSupplierDesk ? "supplier desk" : null,
    permission.canExportFinance ? "finance exports" : null,
    permission.canManageBudgetControls ? "budget controls" : null,
    permission.canManageWorkspace ? "workspace admin" : null,
  ].filter(Boolean);

  return capabilities.length ? capabilities.join(" / ") : "review only";
}
